import Button from '@/components/ui/Button';
import { MessageSquare, Calendar, TrendingUp } from 'lucide-react';

export default function AnalysisHistory({ analyses, onSelectAnalysis, isLoading }) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border border-gray-300 border-t-blue-600" />
      </div>
    );
  }

  if (!analyses || analyses.length === 0) {
    return (
      <div className="text-center py-12">
        <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 mb-2">No analyses yet</p>
        <p className="text-xs text-gray-400">Start by analyzing a conversation in the New Analysis tab</p>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getScoreBgColor = (score) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getScoreTextColor = (score) => {
    if (score >= 80) return 'text-green-700';
    if (score >= 60) return 'text-yellow-700';
    return 'text-red-700';
  };

  return (
    <div className="w-full space-y-4">
      <h3 className="text-sm font-semibold text-gray-900">Analysis History</h3>
      
      <div className="space-y-3">
        {analyses.map((analysis) => (
          <button
            key={analysis.id}
            onClick={() => onSelectAnalysis(analysis)}
            className="w-full text-left p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors group"
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                  {analysis.conversation_type === 'call_transcript' ? (
                    <span className="text-sm">📞</span>
                  ) : (
                    <span className="text-sm">📧</span>
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 group-hover:text-blue-600">
                    {analysis.conversation_type === 'call_transcript' ? 'Call Transcript' : 'Email Thread'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {analysis.industry && `${analysis.industry.charAt(0).toUpperCase() + analysis.industry.slice(1)} • `}
                    {formatDate(analysis.created_at)}
                  </p>
                </div>
              </div>
              <div className={`${getScoreBgColor(analysis.overall_score)} ${getScoreTextColor(analysis.overall_score)} px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1`}>
                <TrendingUp className="w-3 h-3" />
                {analysis.overall_score}
              </div>
            </div>

            {/* Score Summary */}
            <div className="grid grid-cols-3 gap-2 ml-11">
              <div className="text-xs">
                <p className="text-gray-500">Discovery</p>
                <p className="font-semibold text-gray-900">{analysis.discovery_score}</p>
              </div>
              <div className="text-xs">
                <p className="text-gray-500">Objections</p>
                <p className="font-semibold text-gray-900">{analysis.objection_handling_score}</p>
              </div>
              <div className="text-xs">
                <p className="text-gray-500">Closing</p>
                <p className="font-semibold text-gray-900">{analysis.closing_score}</p>
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
